import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import Modal from '../components/Modal.tsx';
import { api, errorMessage } from '../lib/api.ts';
import { cssVars } from '../lib/css.ts';
import { uploadAssets } from '../lib/uploadAssets.ts';
import { isFurniture } from '../lib/assetFurniture.ts';
import { useToast } from '../lib/useToast.ts';
import type { BankItem } from '../../shared/deck.ts';

/** The one toast for the whole room — see the note at its hook call. */
type ToastPush = ReturnType<typeof useToast>['pushToast'];
import { Trash, Plus, Search, Copy, Square, CheckSquare, ZoomIn } from '../lib/icons.tsx';
import Toast from '../components/Toast.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';
import AssetPreviewModal from '../components/AssetPreviewModal.tsx';
import CategorySidebar from '../components/CategorySidebar.tsx';
import TileSizeControl, { useTileSize } from '../components/TileSizeControl.tsx';
import { useBank } from '../components/bank/useBank.ts';
import { bankCategoryTree, isArchived, isUnder, labelFor, matchesQuery } from '../components/bank/bankShared.ts';

const ACCEPT = '.png,.jpg,.jpeg,.webp,.gif,.svg,.mp4,.webm,image/*,video/mp4,video/webm';
const VIDEO_RE = /\.(mp4|webm)$/i;
const TILE_MIN = { sm: '110px', md: '150px', lg: '210px' };

/**
 * The deck editor's Assets picking room.
 *
 * A picking room, not a drawer (2026-08-03). It was a 380px slide-out holding a
 * two-column grid with no search, no filter and no grouping, while the same
 * app's add-slide gallery and media picker get the full `.modal--add-slide`
 * shell for the same job. Three things it now has to do that the drawer could
 * not:
 *
 *  - **Say which pile you are looking at.** The two tabs are the shared `.seg`
 *    control with counts, and each carries a sentence saying what the current
 *    answer means. A user whose reference point is Keynote has no reason to
 *    know this deck has its own asset folder at all.
 *  - **Be navigable.** Search, a type filter, an Unused filter driven by the
 *    server's usage scan, a size control, and — on the bank tab — the same
 *    category rail the Library page uses, which only fits now there is room.
 *  - **Make the action obvious.** Ticking selects; nothing happens to the deck
 *    or the composer until a footer button is pressed. Clicking a thumbnail
 *    used to silently copy a `@assets/…` string to the clipboard for the user
 *    to paste into chat themselves, which is the one asset path in the app that
 *    never reached the composer.
 *
 * Adding hands off to chat rather than editing the slide directly: placing a
 * picture is a layout decision, and the model re-flows the slide around it.
 * "Add to slide" then STARTS that turn (lib/assetAsk.js); "Add to chat" only
 * drops the refs into what the user is writing.
 */

/** One file already in the deck's assets/ directory. */
export type DeckAsset = {
  name: string;
  size: number;
  mtime?: number;
  /** Slide ids that reference it. Absent until the usage scan completes. */
  usedBy?: string[];
};

/** A pick RESOLVED to a file that exists in this deck — what the ask is
 *  composed from. A bank pick becomes one of these only after it is copied in. */
export type ResolvedAsset = { name: string; kind?: 'image' | 'video' };

/** A pick, from either tab. A bank pick is an id until it has been copied into
 *  the deck, which is why both shapes carry `kind` and a display `name`. */
export type PickedAsset = {
  key: string;
  name: string;
  label?: string;
  kind?: 'image' | 'video';
  /** Which tab it was ticked on. A bank pick is an id until it has been copied
   *  into the deck, which is what the selection bar resolves before either verb
   *  composes its text. */
  source?: 'deck' | 'bank';
  /** Present for a bank pick — the id to copy in. */
  bankId?: string;
  id?: string;
};

/** What the delete confirm needs. Matches ConfirmModal's own props, minus the
 *  handlers the room supplies. */
type ConfirmSpec = { title: string; body: string; danger?: boolean; confirmLabel?: string; onConfirm: () => void };

type DeckAssetsState = {
  status: 'loading' | 'ready' | 'error';
  assets: DeckAsset[];
  usageComplete: boolean;
  error: string | null;
};

export default function AssetsModal({
  slug,
  activeSlide,
  chatBusy,
  onAddToSlide,
  onAddToChat,
  onClose,
}: {
  slug: string;
  /** The slide the picks would land on; null when none is focused. */
  activeSlide?: { id: string } | null;
  chatBusy?: boolean;
  onAddToSlide: (assets: ResolvedAsset[]) => void;
  onAddToChat: (assets: ResolvedAsset[]) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState('deck'); // 'deck' | 'bank'
  // Click order — the ask names them in it.
  const [picked, setPicked] = useState<PickedAsset[]>([]);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  // One toast for the whole room. The hook replaces its message on each push,
  // so a per-tab hook would let an upload result and a copy failure sit on
  // screen together, each thinking it was the only one.
  const { toast, pushToast, runUndo, dismissToast } = useToast();

  // Both datasets live here, not in the tabs, because the TABS carry counts:
  // a count that only appears once you have opened the tab is no use for
  // deciding which tab to open. It also means a bank pick copied into the deck
  // refreshes the deck grid behind the tab.
  const deck = useDeckAssets(slug);
  const { state: bank } = useBank();
  const bankMedia = useMemo(
    () => bank.items.filter((it) => it.category !== 'facts' && it.file && !isArchived(it)),
    [bank.items],
  );
  const deckCount = deck.state.assets.filter((a) => !isFurniture(a.name)).length;

  const isPicked = useCallback((key: string) => picked.some((p) => p.key === key), [picked]);
  const toggle = useCallback((entry: PickedAsset) => {
    setPicked((list) =>
      list.some((p) => p.key === entry.key) ? list.filter((p) => p.key !== entry.key) : [...list, entry],
    );
  }, []);

  // Esc clears a live search before it closes the room, then clears a
  // selection, then closes — the same cascade the add-slide gallery uses, so a
  // half-finished pick is never thrown away by the key that means "back".
  //
  // The delete confirm listens on `document` too and we registered first, so
  // without the guard one Escape would cancel the confirm AND close the room
  // out from under it. A nested dialog owns the key while it is open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.modal--confirm')) return;
      if (searchRef.current && document.activeElement === searchRef.current && searchRef.current.value) {
        setQuery('');
        return;
      }
      if (picked.length) {
        setPicked([]);
        return;
      }
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, picked.length]);

  // Switching tabs clears the query but keeps the selection: picking two bank
  // logos and a deck photo for one slide is a reasonable thing to want.
  function pickTab(next: 'deck' | 'bank') {
    setTab(next);
    setQuery('');
  }

  return (
    <Modal ariaLabel="Assets" onClose={onClose} className="modal--add-slide assets-room">
      <header className="modal__header--add-slide">
        <h3 className="modal__title modal__title--sm" id="assets-room-title">
          Assets
        </h3>
        <div className="seg" role="tablist" aria-label="Which assets">
          <TabBtn
            active={tab === 'deck'}
            onClick={() => pickTab('deck')}
            label="In this deck"
            count={deck.state.status === 'ready' ? deckCount : 0}
          />
          <TabBtn
            active={tab === 'bank'}
            onClick={() => pickTab('bank')}
            label="RKH bank"
            count={bank.status === 'ready' ? bankMedia.length : 0}
          />
        </div>
        <span className="add-slide__spacer" />
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
          Close
        </button>
      </header>

      {tab === 'deck' ? (
        <DeckTab
          slug={slug}
          state={deck.state}
          reload={deck.reload}
          query={query}
          onQuery={setQuery}
          searchRef={searchRef}
          isPicked={isPicked}
          onToggle={toggle}
          busy={adding}
          pushToast={pushToast}
        />
      ) : (
        <BankTab
          state={bank}
          media={bankMedia}
          query={query}
          onQuery={setQuery}
          searchRef={searchRef}
          isPicked={isPicked}
          onToggle={toggle}
          busy={adding}
        />
      )}

      <SelectionBar
        picked={picked}
        slug={slug}
        activeSlide={activeSlide}
        chatBusy={chatBusy}
        adding={adding}
        setAdding={setAdding}
        onClear={() => setPicked([])}
        onDeckChanged={deck.reload}
        onAddToSlide={onAddToSlide}
        onAddToChat={onAddToChat}
        pushToast={pushToast}
      />
      <Toast toast={toast} onUndo={runUndo} onDismiss={dismissToast} />
    </Modal>
  );
}

/** The count rides inline in the label rather than in its own element: there is
 *  already a `.view-nav__count` for the page-level sub-nav idiom, and a third
 *  count class would be one too many. */
function TabBtn({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  /** Shown on the tab itself: a count you only see after opening the tab is no
   *  use for deciding which tab to open. */
  count: number;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={'seg__btn' + (active ? ' is-active' : '')}
      onClick={onClick}
    >
      {label}
      {count != null && <span className="muted"> · {count}</span>}
    </button>
  );
}

/** This deck's media, with the reload every mutating action needs. Lifted out
 *  of the tab so the tab strip can count it before the tab is opened. */
function useDeckAssets(slug: string) {
  const [state, setState] = useState<DeckAssetsState>({
    status: 'loading',
    assets: [],
    usageComplete: false,
    error: null,
  });
  const reload = useCallback(async () => {
    try {
      const data = await api.get<{ assets?: DeckAsset[]; usageComplete?: boolean }>(`/api/assets/${slug}`);
      setState({
        status: 'ready',
        assets: data.assets || [],
        usageComplete: !!data.usageComplete,
        error: null,
      });
    } catch (err) {
      setState({ status: 'error', assets: [], usageComplete: false, error: errorMessage(err) });
    }
  }, [slug]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { state, reload };
}

/* ---------- This deck ---------- */

/** What both tabs need to drive the shared search box and the tick state. */
type TabShared = {
  query: string;
  onQuery: (q: string) => void;
  searchRef: RefObject<HTMLInputElement>;
  isPicked: (key: string) => boolean;
  onToggle: (asset: PickedAsset) => void;
  busy?: boolean;
};

function DeckTab({
  slug,
  state,
  reload,
  query,
  onQuery,
  searchRef,
  isPicked,
  onToggle,
  busy,
  pushToast,
}: TabShared & {
  slug: string;
  state: DeckAssetsState;
  reload: () => Promise<void>;
  pushToast: ToastPush;
}) {
  const [kind, setKind] = useState('all'); // 'all' | 'image' | 'video'
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [tileSize, setTileSize] = useTileSize('studio.assetTileSize');
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const load = reload;

  async function upload(fileList: FileList | File[] | null) {
    const files: File[] = Array.from(fileList ?? []);
    if (!files.length) return;
    setUploading(true);
    try {
      const { uploaded, errors } = await uploadAssets(slug, files);
      if (errors.length) {
        pushToast(`Some files were skipped: ${errors.map((e) => `${e.name} (${e.error})`).join(', ')}`, {
          tone: 'error',
        });
      } else if (uploaded.length) {
        pushToast(`Added ${uploaded.length} file${uploaded.length === 1 ? '' : 's'} to this deck`);
      }
      await load();
    } catch (err) {
      pushToast('Upload failed: ' + errorMessage(err), { tone: 'error' });
    } finally {
      setUploading(false);
    }
  }

  // Two things this has to be straight about, because it is the one action here
  // that destroys something.
  //
  // It REMOVES FROM THIS DECK. A deck asset is the deck's own copy, so the word
  // "delete" overstates it: a picture that came from the RKH bank is still in
  // the bank afterwards, and every other deck using it is untouched. Calling
  // that "delete" invites someone to think they are about to damage the shared
  // library and stop — or worse, to think they have tidied the bank when they
  // have not. The reassurance is worded so it holds whether or not the file
  // came from the bank in the first place.
  //
  // And it names what breaks: the count the server gave us is spent here rather
  // than only shown on the card, and it never claims "nothing uses it" unless
  // the scan could actually prove that.
  function askDelete(asset: DeckAsset) {
    const used = asset.usedBy?.length || 0;
    const where = used ? (asset.usedBy ?? []).map((i) => Number(i) + 1).join(', ') : '';
    const impact = used
      ? `It is used on slide ${where}, and ${used === 1 ? 'that slide' : 'those slides'} will stop showing it.`
      : canTrustUnused
        ? 'No slide in this deck uses it.'
        : 'Studio could not work out which slides use it.';
    setConfirm({
      title: `Remove ${asset.name} from this deck?`,
      body: `${impact} This only removes this deck's copy: the RKH bank and every other deck are left as they are. It cannot be undone.`,
      danger: true,
      confirmLabel: 'Remove from deck',
      onConfirm: async () => {
        try {
          await api.delete(`/api/assets/${slug}?name=${encodeURIComponent(asset.name)}`);
          setConfirm(null);
          await load();
          pushToast(`Removed ${asset.name} from this deck`);
        } catch (err) {
          setConfirm(null);
          pushToast('Could not remove it: ' + errorMessage(err), { tone: 'error' });
        }
      },
    });
  }

  function copyRef(name: string) {
    navigator.clipboard.writeText(`@assets/${name}`).then(
      () => {
        setCopied(name);
        setTimeout(() => setCopied((c) => (c === name ? null : c)), 1400);
      },
      () => pushToast('Could not copy to the clipboard', { tone: 'error' }),
    );
  }

  const q = query.trim().toLowerCase();
  // Two different unknowns, and both have to suppress "Unused". The server
  // omits `usedBy` when the manifest won't parse, and clears `usageComplete`
  // when a slide reaches its images through a wildcard glob that names no
  // file. Either way "nothing uses this" is a guess, and it is the guess that
  // gets someone to delete a live asset.
  const usageKnown = state.assets.some((a) => Array.isArray(a.usedBy));
  const canTrustUnused = usageKnown && state.usageComplete;
  // Which asset the viewer is showing, as an index into `visible` — so its
  // arrows walk exactly what the filters have left on screen, and never wander
  // into files the user has filtered out.
  const [magnified, setMagnified] = useState<number | null>(null);
  const visible = state.assets.filter((a) => {
    if (isFurniture(a.name)) return false;
    const isVideo = VIDEO_RE.test(a.name);
    if (kind === 'image' && isVideo) return false;
    if (kind === 'video' && !isVideo) return false;
    if (unusedOnly && (a.usedBy?.length || 0) > 0) return false;
    if (q && !a.name.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <>
      <p className="assets-room__hint muted small">
        Images and video that belong to this deck. Anything you add here stays in this deck.
      </p>

      <div className="assets-room__toolbar">
        <label className="bank__search">
          <Search />
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder="Search this deck's files"
            aria-label="Search this deck's files"
            onChange={(e) => onQuery(e.target.value)}
          />
        </label>
        <div className="seg" role="group" aria-label="File type">
          {(
            [
              ['all', 'All'],
              ['image', 'Images'],
              ['video', 'Video'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={'seg__btn' + (kind === v ? ' is-active' : '')}
              aria-pressed={kind === v}
              onClick={() => setKind(v)}
            >
              {label}
            </button>
          ))}
        </div>
        {canTrustUnused && (
          <button
            type="button"
            className={'chip' + (unusedOnly ? ' chip--active' : '')}
            aria-pressed={unusedOnly}
            onClick={() => setUnusedOnly((v) => !v)}
            title="Show only files no slide uses"
          >
            Unused
          </button>
        )}
        <span className="add-slide__spacer" />
        <TileSizeControl value={tileSize} onChange={setTileSize} />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          <Plus /> {uploading ? 'Adding…' : 'Add files'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            upload(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div
        className={'assets-room__body' + (dragOver ? ' assets-room__body--over' : '')}
        style={cssVars({ '--tile-min': TILE_MIN[tileSize] })}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const files = Array.from(e.dataTransfer?.files || []).filter(
            (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
          );
          if (files.length) upload(files);
        }}
      >
        {state.status === 'loading' && <p className="muted small">Loading…</p>}
        {state.status === 'error' && (
          <div className="error">
            <strong>Could not load this deck's files</strong>
            <p className="muted small">{state.error}</p>
          </div>
        )}
        {state.status === 'ready' && visible.length === 0 && (
          <p className="muted small assets-room__empty">
            {state.assets.length === 0
              ? 'Nothing here yet. Drop in images or video and they become part of this deck.'
              : 'Nothing matches those filters.'}
          </p>
        )}
        {state.status === 'ready' && visible.length > 0 && (
          <ul className="assets-room__grid" role="list">
            {visible.map((a, i) => {
              const key = `deck:${a.name}`;
              const used = a.usedBy?.length || 0;
              return (
                <AssetCard
                  key={key}
                  onMagnify={() => setMagnified(i)}
                  picked={isPicked(key)}
                  disabled={busy}
                  label={a.name}
                  title={
                    Array.isArray(a.usedBy) && used
                      ? `${a.name} — used on slide${used === 1 ? '' : 's'} ${a.usedBy.map((i) => i + 1).join(', ')}`
                      : a.name
                  }
                  badge={used ? `Used on ${used} slide${used === 1 ? '' : 's'}` : canTrustUnused ? 'Unused' : null}
                  badgeTone={used ? 'ok' : 'muted'}
                  onToggle={() =>
                    onToggle({
                      key,
                      source: 'deck',
                      name: a.name,
                      label: a.name,
                      kind: VIDEO_RE.test(a.name) ? 'video' : 'image',
                    })
                  }
                  media={
                    VIDEO_RE.test(a.name) ? (
                      <video
                        src={`/preview/${slug}/assets/${encodeURIComponent(a.name)}`}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                        onMouseLeave={(e) => e.currentTarget.pause()}
                      />
                    ) : (
                      <img src={`/preview/${slug}/assets/${encodeURIComponent(a.name)}`} alt="" loading="lazy" />
                    )
                  }
                  actions={
                    <>
                      <button
                        type="button"
                        className="iconbtn"
                        onClick={() => copyRef(a.name)}
                        aria-label={`Copy the @assets/${a.name} reference`}
                        title={copied === a.name ? 'Copied' : 'Copy reference'}
                      >
                        <Copy />
                      </button>
                      <button
                        type="button"
                        className="iconbtn"
                        onClick={() => askDelete(a)}
                        aria-label={`Remove ${a.name} from this deck`}
                        title="Remove from this deck"
                      >
                        <Trash />
                      </button>
                    </>
                  }
                />
              );
            })}
          </ul>
        )}
      </div>

      {magnified != null && visible.length > 0 && (
        <AssetPreviewModal
          items={visible.map((a) => ({
            key: a.name,
            src: `/preview/${slug}/assets/${encodeURIComponent(a.name)}`,
            kind: VIDEO_RE.test(a.name) ? 'video' : 'image',
            label: a.name,
            size: a.size,
          }))}
          index={Math.min(magnified, visible.length - 1)}
          onClose={() => setMagnified(null)}
        />
      )}

      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
    </>
  );
}

/* ---------- RKH bank ---------- */

function BankTab({
  state,
  media,
  query,
  onQuery,
  searchRef,
  isPicked,
  onToggle,
  busy,
}: TabShared & {
  state: ReturnType<typeof useBank>['state'];
  /** The bank's image/video items, already filtered from its facts. */
  media: BankItem[];
}) {
  // null = every category
  const [cat, setCat] = useState<string | null>(null);
  const [tileSize, setTileSize] = useTileSize('studio.assetTileSize');
  // Index into `visible`, so the viewer's arrows walk the open category (or the
  // search results), not the whole bank.
  const [magnified, setMagnified] = useState<number | null>(null);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  const cats = useMemo(() => state.categories.filter((c) => c !== 'facts'), [state.categories]);
  const tree = useMemo(() => bankCategoryTree(cats, media), [cats, media]);

  // A search crosses the whole bank rather than filtering the open category:
  // looking for a logo you can't place is exactly when you don't know which
  // shelf it is on. Same rule as the Library page.
  const visible = searching
    ? media.filter((it) => matchesQuery(it, q))
    : cat === null
      ? media
      : media.filter((it) => isUnder(it.category, cat));

  if (state.status === 'loading') return <p className="muted small">Loading the bank…</p>;
  if (state.status === 'error') {
    return (
      <div className="error">
        <strong>Could not load the RKH bank</strong>
        <p className="muted small">{state.error}</p>
      </div>
    );
  }

  return (
    <>
      <p className="assets-room__hint muted small">
        Shared RKH assets, available to every deck. Adding one copies it into this deck first. Manage the bank from the
        Library page.
      </p>

      <div className="assets-room__toolbar">
        <label className="bank__search">
          <Search />
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder="Search the bank: name, role, tag"
            aria-label="Search the bank"
            onChange={(e) => onQuery(e.target.value)}
          />
        </label>
        <span className="add-slide__spacer" />
        <TileSizeControl value={tileSize} onChange={setTileSize} />
      </div>

      <div className="assets-room__body assets-room__body--rail">
        <CategorySidebar
          tree={tree}
          active={searching ? null : cat}
          allCount={media.length}
          uncategorisedCount={0}
          uncategorisedKey="__none"
          onSelect={(next) => {
            setCat(next);
            onQuery('');
          }}
          ariaLabel="Bank categories"
          allLabel="All assets"
        />
        <div className="assets-room__rail-main" style={cssVars({ '--tile-min': TILE_MIN[tileSize] })}>
          {searching && (
            <p className="muted small">
              {visible.length} match{visible.length === 1 ? '' : 'es'} for “{query.trim()}” across the whole bank.
            </p>
          )}
          {visible.length === 0 ? (
            <p className="muted small assets-room__empty">Nothing here.</p>
          ) : (
            <ul className="assets-room__grid" role="list">
              {visible.map((it, i) => {
                const key = `bank:${it.id}`;
                const name = it.meta?.name || it.label;
                return (
                  <AssetCard
                    key={key}
                    picked={isPicked(key)}
                    disabled={busy}
                    label={name}
                    title={it.label}
                    badge={searching ? labelFor(it.category) : it.meta?.role || null}
                    badgeTone="muted"
                    onToggle={() => onToggle({ key, source: 'bank', id: it.id, name, label: name, kind: 'image' })}
                    media={<img src={`/api/bank/file/${it.id}`} alt="" loading="lazy" />}
                    onMagnify={() => setMagnified(i)}
                  />
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {magnified != null && visible.length > 0 && (
        <AssetPreviewModal
          items={visible.map((it) => ({
            key: it.id,
            src: `/api/bank/file/${encodeURIComponent(it.id)}`,
            kind: 'image',
            label: it.meta?.name || it.label,
          }))}
          index={Math.min(magnified, visible.length - 1)}
          onClose={() => setMagnified(null)}
        />
      )}
    </>
  );
}

/* ---------- One card ---------- */

/** A tile whose whole body is the tick: selecting is the only thing a click on
 *  a picture does, so the target is the picture. The icon actions sit in the
 *  meta row, outside the toggle, because copying a reference or deleting a file
 *  are different intentions from picking one.
 *
 *  `onMagnify` is the one exception that still isn't a click on the picture: a
 *  SIBLING button over the thumb, because the thumb is inside a <button> and a
 *  nested one is invalid markup whose keyboard activation would tick the card
 *  instead of opening the viewer. Ticking still selects; only a button acts. */
function AssetCard({
  picked,
  disabled,
  label,
  title,
  badge,
  badgeTone,
  onToggle,
  media,
  actions,
  onMagnify,
}: {
  picked: boolean;
  disabled?: boolean;
  label: string;
  title?: string;
  badge?: ReactNode;
  badgeTone?: string;
  onToggle: () => void;
  /** The thumbnail itself — an <img> or a <video>, built by the tab. */
  media: ReactNode;
  /** Per-card controls (delete, copy). Optional: the bank tab has none. */
  actions?: ReactNode;
  onMagnify?: () => void;
}) {
  return (
    <li className={'picker-card' + (picked ? ' picker-card--picked' : '')} title={title}>
      <button
        type="button"
        className="picker-card__pick"
        onClick={onToggle}
        disabled={disabled}
        aria-pressed={picked}
        aria-label={picked ? `Deselect ${label}` : `Select ${label}`}
      >
        <span className="picker-card__thumb">{media}</span>
        <span className="picker-card__tick" aria-hidden="true">
          {picked ? <CheckSquare /> : <Square />}
        </span>
      </button>
      {onMagnify && (
        <button
          type="button"
          className="asset-magnify"
          title="Take a closer look"
          aria-label={`Preview ${label}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onMagnify();
          }}
        >
          <ZoomIn />
        </button>
      )}
      <div className="picker-card__meta">
        <span className="picker-card__name">{label}</span>
        {actions && <span className="picker-card__actions">{actions}</span>}
      </div>
      {badge && <span className={'picker-card__badge picker-card__badge--' + badgeTone}>{badge}</span>}
    </li>
  );
}

/* ---------- The footer ---------- */

/**
 * What the picks are for. Two verbs, both explicit — the room never acts on a
 * click.
 *
 * "Add to slide" hands a composed ask to the chat, STARTS it, and closes; "Add
 * to chat" drops the refs into whatever the user is already typing and stays
 * open, so a hand-written instruction can name several files. That difference is
 * the point of having two buttons: when both merely wrote text into the composer
 * they read as one button spelled twice (Alex, 2026-08-03). A bank pick has to be
 * copied into the deck first, and that happens HERE rather than on tick: browsing
 * the bank must not litter the deck with files nobody chose.
 */
function SelectionBar({
  picked,
  slug,
  activeSlide,
  chatBusy,
  adding,
  setAdding,
  onClear,
  onDeckChanged,
  onAddToSlide,
  onAddToChat,
  pushToast,
}: {
  picked: PickedAsset[];
  slug: string;
  activeSlide?: { id: string } | null;
  chatBusy?: boolean;
  adding: boolean;
  setAdding: (next: boolean) => void;
  onClear: () => void;
  /** A bank pick copied into the deck changes the deck grid behind the tab. */
  onDeckChanged: () => void;
  onAddToSlide: (assets: ResolvedAsset[]) => void;
  onAddToChat: (assets: ResolvedAsset[]) => void;
  pushToast: ToastPush;
}) {
  const n = picked.length;

  // A bank item is an id until it is a file in this deck. Resolve every pick to
  // a deck filename before either verb composes its text.
  //
  // Sequential because there is no batch copy endpoint, and a failure part-way
  // through is reported rather than swallowed: the copies already made are real
  // files on disk, and telling the user they landed is the difference between a
  // retry and a duplicate.
  async function resolve(): Promise<ResolvedAsset[]> {
    const out: ResolvedAsset[] = [];
    let copied = 0;
    try {
      for (const p of picked) {
        if (p.source === 'deck') {
          out.push({ name: p.name, kind: p.kind });
          continue;
        }
        const res = await api.post<{ filename: string }>(`/api/bank/items/${p.id}/copy-to/${slug}`);
        copied += 1;
        out.push({ name: res.filename, kind: 'image' });
      }
    } catch (err) {
      if (copied) onDeckChanged?.();
      // How many made it before the failure — the toast says so, because
      // "copy failed" after three of five landed is a misleading sentence.
      if (err instanceof Error) (err as Error & { copied?: number }).copied = copied;
      throw err;
    }
    if (copied) onDeckChanged?.();
    return out;
  }

  async function act(fn: (assets: ResolvedAsset[]) => void) {
    if (!n || adding) return;
    setAdding(true);
    try {
      fn(await resolve());
    } catch (err) {
      const copiedSoFar = err instanceof Error ? ((err as Error & { copied?: number }).copied ?? 0) : 0;
      const landed = copiedSoFar
        ? ` ${copiedSoFar} bank file${copiedSoFar === 1 ? '' : 's'} did copy into the deck.`
        : '';
      pushToast(`Could not add those files: ${errorMessage(err)}.${landed}`, { tone: 'error' });
    } finally {
      setAdding(false);
    }
  }

  // The chat is held while a turn runs, so "Add to slide" cannot start one right
  // now — it writes the instruction into the box instead, ready to send. Say so
  // rather than disabling: the button still does something useful, and a dead
  // button would just lose the intent.
  const reason = !n
    ? 'Tick the files you want'
    : !activeSlide
      ? 'Open a slide to add to it'
      : chatBusy
        ? 'Studio is working. This will wait in the chat box, ready to send.'
        : null;

  return (
    <footer className="assets-room__bar">
      <span className="assets-room__count">
        {n ? `${n} selected` : 'Nothing selected'}
        {n > 0 && (
          <button type="button" className="link" onClick={onClear}>
            Clear
          </button>
        )}
      </span>
      <span className="add-slide__spacer" />
      {reason && <span className="muted small">{reason}</span>}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={!n || adding}
        onClick={() => act(onAddToChat)}
        title="Put the file references into the message you are writing, without sending it"
      >
        Add to chat
      </button>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        disabled={!n || !activeSlide || adding}
        onClick={() => act(onAddToSlide)}
        title={
          chatBusy
            ? 'Studio is working — this will wait in the chat box, ready to send'
            : 'Ask the studio to place these on the slide, starting now'
        }
      >
        {adding ? 'Adding…' : n > 1 ? `Add ${n} to slide` : 'Add to slide'}
      </button>
    </footer>
  );
}
